import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Check, Pencil, Trash2, X, Save, Calendar, User, AlertTriangle, MapPin, Clock, FileText } from 'lucide-react';
import ClienteSelector from '../components/ClienteSelector';
import LocalAvulsoCampos, { avulsoVazio, avulsoPreenchido, cadastrarLocalAvulso } from '../components/LocalAvulsoCampos';
import { formatDateOnly, ordenarVisitasRota } from '../lib/utils';

const STATUS_LABEL = {
  rascunho: 'Rascunho',
  em_andamento: 'Em andamento',
  finalizada: 'Finalizada',
};

const fieldClass =
  'w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none';

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
};

const formatDurationMin = (minutos) => {
  if (minutos == null || minutos === '') return null;
  const n = Number(minutos);
  if (!Number.isFinite(n)) return null;
  if (n < 60) return `${Math.round(n)} min`;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const formatKmValue = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
};

/** Base / início-fim de expediente: sem cliente — sempre considerado feito. */
const isParadaBase = (visita) => !visita?.cliente_id;
const visitaEstaFeita = (visita) => isParadaBase(visita) || !!visita?.realizado;

const durationFromParada = (parada) => {
  if (parada.duracao_minutos != null && parada.duracao_minutos !== '') {
    return Number(parada.duracao_minutos);
  }
  if (parada.hora_chegada && parada.hora_saida) {
    try {
      const ms = new Date(parada.hora_saida) - new Date(parada.hora_chegada);
      if (Number.isFinite(ms) && ms >= 0) return ms / 60000;
    } catch {
      /* ignore */
    }
  }
  return null;
};

const RotaDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rota, setRota] = useState(null);
  const [tiposServico, setTiposServico] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddCliente, setShowAddCliente] = useState(false);
  const [addModo, setAddModo] = useState('cliente');
  const [addAvulso, setAddAvulso] = useState(avulsoVazio());
  const [novoClienteId, setNovoClienteId] = useState('');
  const [novoTipoServico, setNovoTipoServico] = useState('');
  const [novoHorarioAgendado, setNovoHorarioAgendado] = useState('');
  const [editandoVisitaId, setEditandoVisitaId] = useState(null);
  const [editClienteId, setEditClienteId] = useState('');
  const [editTipoServico, setEditTipoServico] = useState('');
  const [editHorarioAgendado, setEditHorarioAgendado] = useState('');
  const [removendoVisitaId, setRemovendoVisitaId] = useState(null);
  const [editandoCabecalho, setEditandoCabecalho] = useState(false);
  const [editData, setEditData] = useState('');
  const [editTecnicoId, setEditTecnicoId] = useState('');
  const [savingCabecalho, setSavingCabecalho] = useState(false);

  useEffect(() => {
    if (id) loadRota();
  }, [id]);

  useEffect(() => {
    api.get('/tipos-servico-rota', { params: { ativo: true } })
      .then((res) => setTiposServico(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
    api.get('/users', { params: { funcao: 'tecnico' } })
      .then((res) => setTecnicos(res.data?.users || []))
      .catch(() => {});
  }, []);

  const loadRota = async () => {
    try {
      const rotaRes = await api.get(`/rotas/${id}`);
      setRota(rotaRes.data);
    } catch (err) {
      toast.error('Rota não encontrada');
      navigate('/rotas');
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicaoCabecalho = () => {
    setEditData(rota.data || '');
    setEditTecnicoId(rota.tecnico_id || '');
    setEditandoCabecalho(true);
  };

  const cancelarEdicaoCabecalho = () => {
    setEditandoCabecalho(false);
    setEditData('');
    setEditTecnicoId('');
  };

  const handleSalvarCabecalho = async () => {
    if (!editData) {
      toast.error('Informe a data');
      return;
    }
    if (!editTecnicoId) {
      toast.error('Selecione o técnico');
      return;
    }
    const tecnico = tecnicos.find((t) => t.id === editTecnicoId);
    setSavingCabecalho(true);
    try {
      const { data } = await api.patch(`/rotas/${id}`, {
        data: editData,
        tecnico_id: editTecnicoId,
        tecnico_nome: tecnico?.name || tecnico?.nome || rota.tecnico_nome || '',
      });
      setRota(data);
      setEditandoCabecalho(false);
      toast.success('Rota atualizada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar rota');
    } finally {
      setSavingCabecalho(false);
    }
  };

  const handleMarcarRealizado = async (visitaId, realizado) => {
    try {
      await api.patch(`/rotas/${id}/visitas/${visitaId}`, { realizado: !!realizado });
      toast.success(realizado ? 'Marcado como realizado' : 'Desmarcado');
      loadRota();
    } catch (err) {
      toast.error('Erro ao atualizar');
    }
  };

  const resetAddForm = () => {
    setShowAddCliente(false);
    setAddModo('cliente');
    setAddAvulso(avulsoVazio());
    setNovoClienteId('');
    setNovoTipoServico('');
    setNovoHorarioAgendado('');
  };

  const handleAddCliente = async () => {
    if (addModo === 'avulso') {
      if (!avulsoPreenchido(addAvulso)) {
        toast.error('Informe o nome e o endereço do local');
        return;
      }
    } else if (!novoClienteId) {
      toast.error('Selecione um cliente');
      return;
    }
    if (!novoTipoServico) {
      toast.error('Selecione o tipo de serviço');
      return;
    }
    setSaving(true);
    try {
      let clienteId = novoClienteId;
      if (addModo === 'avulso') {
        const criado = await cadastrarLocalAvulso(addAvulso);
        clienteId = criado.id;
      }
      const body = { cliente_id: clienteId, tipo_servico: novoTipoServico };
      if (novoHorarioAgendado) body.horario_agendado = novoHorarioAgendado;
      await api.post(`/rotas/${id}/visitas`, body);
      toast.success(addModo === 'avulso' ? 'Local avulso adicionado à rota!' : 'Cliente adicionado à rota!');
      resetAddForm();
      loadRota();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar');
    } finally {
      setSaving(false);
    }
  };

  const iniciarEdicaoVisita = (visita) => {
    setEditandoVisitaId(visita.id);
    setEditClienteId(visita.cliente_id || '');
    setEditTipoServico(visita.tipo_servico || '');
    setEditHorarioAgendado(visita.horario_agendado || '');
  };

  const cancelarEdicaoVisita = () => {
    setEditandoVisitaId(null);
    setEditClienteId('');
    setEditTipoServico('');
    setEditHorarioAgendado('');
  };

  const handleSalvarEdicaoVisita = async (visitaId) => {
    if (!editClienteId) {
      toast.error('Selecione um cliente');
      return;
    }
    if (!editTipoServico) {
      toast.error('Selecione o tipo de serviço');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/rotas/${id}/visitas/${visitaId}`, {
        cliente_id: editClienteId,
        tipo_servico: editTipoServico,
        horario_agendado: editHorarioAgendado || null,
      });
      toast.success('Visita atualizada!');
      cancelarEdicaoVisita();
      loadRota();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar visita');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoverVisita = async (visitaId) => {
    if (!window.confirm('Remover este cliente da rota? Só é possível remover visitas ainda não realizadas.')) return;
    setRemovendoVisitaId(visitaId);
    try {
      await api.delete(`/rotas/${id}/visitas/${visitaId}`);
      toast.success('Cliente removido da rota');
      loadRota();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover');
    } finally {
      setRemovendoVisitaId(null);
    }
  };

  const labelTipo = (visita) =>
    visita.tipo_servico_label
    || tiposServico.find((t) => t.slug === visita.tipo_servico)?.label
    || visita.tipo_servico
    || null;

  if (loading || !rota) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const visitas = rota.visitas || [];
  const podeEditar = rota.status !== 'finalizada';
  const clientesNaRota = visitas.map((v) => v.cliente_id).filter(Boolean);
  const visitasSemRelatorio = visitas.filter(
    (v) => v.cliente_id && visitaEstaFeita(v) && !v.manutencao_id && !isParadaBase(v)
  );
  const paradasOrdenadas = [...(rota.paradas || [])].sort((a, b) => {
    const ta = a?.hora_chegada ? new Date(a.hora_chegada).getTime() : 0;
    const tb = b?.hora_chegada ? new Date(b.hora_chegada).getTime() : 0;
    return ta - tb;
  });

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <button
          onClick={() => navigate('/rotas')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-5 h-5" /> Voltar
        </button>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            rota.status === 'rascunho' ? 'bg-amber-100 text-amber-800' :
            rota.status === 'em_andamento' ? 'bg-emerald-100 text-emerald-800' :
            'bg-slate-100 text-slate-600'
          }`}>
            {STATUS_LABEL[rota.status]}
          </span>
          <span className="text-xs text-slate-500">Iniciar e finalizar: técnico pelo app</span>
        </div>
      </div>

      {rota.reconciliacao_pendente_admin && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">Reconciliação de KM pendente</p>
            <p className="text-sm text-amber-800 mt-0.5">
              O KM final informado não pôde ser reconciliado automaticamente com o trajeto GPS.
              Revise os valores de KM ou os trechos calculados antes de confiar no total da rota.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm p-6">
        {editandoCabecalho ? (
          <div className="space-y-4">
            <h1 className="text-xl font-bold text-slate-900">Editar rota</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1.5">
                  <Calendar className="w-4 h-4 text-slate-400" /> Data
                </label>
                <input
                  type="date"
                  value={editData}
                  onChange={(e) => setEditData(e.target.value)}
                  className={fieldClass}
                  required
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1.5">
                  <User className="w-4 h-4 text-slate-400" /> Técnico
                </label>
                <select
                  value={editTecnicoId}
                  onChange={(e) => setEditTecnicoId(e.target.value)}
                  className={fieldClass}
                  required
                >
                  <option value="">Selecione o técnico...</option>
                  {tecnicos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || t.nome}
                    </option>
                  ))}
                  {editTecnicoId && !tecnicos.some((t) => t.id === editTecnicoId) && (
                    <option value={editTecnicoId}>{rota.tecnico_nome || editTecnicoId}</option>
                  )}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSalvarCabecalho}
                disabled={savingCabecalho || !editData || !editTecnicoId}
                className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {savingCabecalho ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                onClick={cancelarEdicaoCabecalho}
                disabled={savingCabecalho}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900 mb-1">
                Rota — {formatDateOnly(rota.data)}
              </h1>
              <p className="text-slate-600">
                {rota.tecnico_nome} · {rota.carro_placa || 'Sem carro'} · {Number(rota.km_total || 0).toFixed(0)} km
              </p>
            </div>
            {podeEditar && (
              <button
                onClick={iniciarEdicaoCabecalho}
                className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                title="Editar data e técnico"
              >
                <Pencil className="w-4 h-4" /> Editar
              </button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-slate-900">Clientes da rota</h2>
          {podeEditar && (
            <button
              onClick={() => {
                if (showAddCliente) resetAddForm();
                else setShowAddCliente(true);
              }}
              className="text-emerald-600 font-medium flex items-center gap-2 hover:underline"
            >
              <Plus className="w-4 h-4" /> Adicionar cliente
            </button>
          )}
        </div>

        {visitasSemRelatorio.length > 0 && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              {visitasSemRelatorio.length === 1
                ? `A visita ${visitasSemRelatorio[0].cliente_codigo || visitasSemRelatorio[0].cliente_nome} está marcada como feita, mas o relatório não chegou ao sistema.`
                : `${visitasSemRelatorio.length} visitas estão marcadas como feitas sem relatório sincronizado.`}
            </p>
          </div>
        )}

        {showAddCliente && (
          <div className="mb-6 p-4 bg-slate-50 rounded-xl space-y-4">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setAddModo('cliente')}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  addModo !== 'avulso'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Cliente cadastrado
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddModo('avulso');
                  setNovoClienteId('');
                  if (!novoTipoServico) {
                    const slug = tiposServico.find((t) => t.slug === 'visita_tecnica')?.slug;
                    if (slug) setNovoTipoServico(slug);
                  }
                }}
                className={`px-3 py-1.5 rounded-md transition-colors inline-flex items-center gap-1 ${
                  addModo === 'avulso'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <MapPin className="w-3 h-3" /> Local avulso
              </button>
            </div>

            {addModo === 'avulso' ? (
              <LocalAvulsoCampos value={addAvulso} onChange={setAddAvulso} compact />
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cliente</label>
                <ClienteSelector
                  value={novoClienteId || null}
                  onChange={(cliente) => setNovoClienteId(cliente?.id || '')}
                  excludeIds={clientesNaRota}
                />
              </div>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div className="w-48">
                <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de serviço</label>
                <select
                  value={novoTipoServico}
                  onChange={(e) => setNovoTipoServico(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                >
                  <option value="">Selecione...</option>
                  {tiposServico.map((t) => (
                    <option key={t.id || t.slug} value={t.slug}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="w-32">
                <label className="block text-sm font-medium text-slate-700 mb-1">Horário agendado (opcional)</label>
                <input type="time" value={novoHorarioAgendado} onChange={(e) => setNovoHorarioAgendado(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <button
                onClick={handleAddCliente}
                disabled={
                  saving
                  || !novoTipoServico
                  || (addModo === 'avulso' ? !avulsoPreenchido(addAvulso) : !novoClienteId)
                }
                className="bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                Adicionar
              </button>
              <button
                onClick={resetAddForm}
                className="px-4 py-2 text-slate-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        <ul className="space-y-3">
          {ordenarVisitasRota(visitas).map((visita, index) => {
            const feita = visitaEstaFeita(visita);
            const paradaBase = isParadaBase(visita);
            return (
            <li
              key={visita.id}
              className={`flex flex-col gap-3 p-4 rounded-xl border ${
                feita ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
              }`}
            >
              {editandoVisitaId === visita.id ? (
                <div className="flex flex-wrap items-end gap-3">
                  <span className="text-slate-500 font-medium w-8">{index + 1}.</span>
                  <div className="flex-1 min-w-[240px]">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Cliente</label>
                    <ClienteSelector
                      value={editClienteId || null}
                      onChange={(cliente) => setEditClienteId(cliente?.id || '')}
                      excludeIds={clientesNaRota.filter((cid) => cid !== editClienteId)}
                    />
                  </div>
                  <div className="w-48">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de serviço</label>
                    <select
                      value={editTipoServico}
                      onChange={(e) => setEditTipoServico(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    >
                      <option value="">Selecione...</option>
                      {tiposServico.map((t) => (
                        <option key={t.id || t.slug} value={t.slug}>{t.label}</option>
                      ))}
                      {editTipoServico && !tiposServico.some((t) => t.slug === editTipoServico) && (
                        <option value={editTipoServico}>
                          {visita.tipo_servico_label || editTipoServico} (inativo)
                        </option>
                      )}
                    </select>
                  </div>
                  <div className="w-32">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Horário agendado</label>
                    <input
                      type="time"
                      value={editHorarioAgendado}
                      onChange={(e) => setEditHorarioAgendado(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    />
                  </div>
                  <button
                    onClick={() => handleSalvarEdicaoVisita(visita.id)}
                    disabled={saving || !editClienteId || !editTipoServico}
                    className="bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                  >
                    Salvar
                  </button>
                  <button
                    onClick={cancelarEdicaoVisita}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-slate-500 font-medium w-8">{index + 1}.</span>
                      <div>
                        <p className="font-medium text-slate-900">
                          {visita.cliente_nome}
                          {String(visita.cliente_codigo || '').startsWith('AVULSO-') && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-100 text-amber-800">
                              Avulso
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500">
                          {visita.cliente_codigo}
                          {labelTipo(visita) ? ` · ${labelTipo(visita)}` : ''}
                          {visita.horario_agendado ? ` · ${visita.horario_agendado}` : ''}
                          {visita.km_chegada != null && visita.km_chegada !== ''
                            ? ` · KM ${Number(visita.km_chegada).toFixed(0)}`
                            : ''}
                        </p>
                        {visita.manutencao_id ? (
                          <Link
                            to={`/relatorios-manutencao/${visita.manutencao_id}`}
                            className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Relatório {visita.numero_relatorio || 'técnico'}
                          </Link>
                        ) : feita && !paradaBase ? (
                          <p className="mt-1 text-sm font-medium text-amber-700">
                            Sem relatório sincronizado
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {podeEditar && !feita && !paradaBase && (
                        <>
                          <button
                            onClick={() => iniciarEdicaoVisita(visita)}
                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-200 transition-colors"
                            title="Editar cliente/horário"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemoverVisita(visita.id)}
                            disabled={removendoVisitaId === visita.id}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Remover da rota"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {paradaBase ? (
                        <span className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-emerald-500 text-white">
                          <Check className="w-4 h-4" />
                          Feito
                        </span>
                      ) : podeEditar && visita.realizado !== undefined ? (
                        <button
                          onClick={() => handleMarcarRealizado(visita.id, !visita.realizado)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                            visita.realizado
                              ? 'bg-emerald-500 text-white'
                              : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <Check className="w-4 h-4" />
                          {visita.realizado ? 'Feito' : 'Marcar feito'}
                        </button>
                      ) : !podeEditar ? (
                        <span className={feita ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
                          {feita ? 'Realizado' : 'Não realizado'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </li>
            );
          })}
        </ul>
        {visitas.length === 0 && (
          <p className="text-center text-slate-500 py-8">Nenhum cliente na rota. Adicione na criação ou aqui.</p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Paradas do dia</h2>
        <ul className="space-y-3">
          {paradasOrdenadas.map((parada, index) => {
            const duracao = durationFromParada(parada);
            const duracaoLabel = formatDurationMin(duracao);
            const trechoKm = formatKmValue(parada.distancia_trecho_km);
            const kmCalc = formatKmValue(parada.km_calculado);
            const hasCoords = parada.latitude != null && parada.longitude != null;
            const endereco =
              parada.local_nome
              || parada.endereco
              || parada.endereco_aproximado
              || null;
            const vinculada = !!(parada.cliente_id || parada.manutencao_id || parada.cliente_nome);

            return (
              <li
                key={parada.id || `${parada.hora_chegada}-${index}`}
                className={`p-4 rounded-xl border ${
                  vinculada ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-slate-500 font-medium w-8 shrink-0 pt-0.5">{index + 1}.</span>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {formatTime(parada.hora_chegada)}
                        {' → '}
                        {formatTime(parada.hora_saida)}
                        {duracaoLabel ? (
                          <span className="text-slate-500 font-normal">({duracaoLabel})</span>
                        ) : null}
                      </span>
                    </div>

                    {hasCoords && (
                      <p className="text-sm text-slate-600 flex items-start gap-1.5">
                        <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                        <span>
                          {endereco ? (
                            <>
                              {endereco}
                              <span className="text-slate-400">
                                {' · '}
                                {Number(parada.latitude).toFixed(5)}, {Number(parada.longitude).toFixed(5)}
                              </span>
                            </>
                          ) : (
                            <>
                              {Number(parada.latitude).toFixed(5)}, {Number(parada.longitude).toFixed(5)}
                            </>
                          )}
                          <a
                            href={`https://www.google.com/maps?q=${parada.latitude},${parada.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-emerald-600 hover:underline font-medium"
                          >
                            Mapa
                          </a>
                        </span>
                      </p>
                    )}

                    {(trechoKm != null || kmCalc != null) && (
                      <p className="text-sm text-slate-600">
                        {trechoKm != null && (
                          <span>Trecho: {trechoKm} km</span>
                        )}
                        {trechoKm != null && kmCalc != null && (
                          <span className="text-slate-400"> · </span>
                        )}
                        {kmCalc != null && (
                          <span>KM acumulado: {kmCalc}</span>
                        )}
                      </p>
                    )}

                    {vinculada ? (
                      <p className="text-sm text-emerald-800">
                        {parada.cliente_nome || 'Cliente vinculado'}
                        {parada.manutencao_id && (
                          <>
                            {' · '}
                            <Link
                              to={`/relatorios-manutencao/${parada.manutencao_id}`}
                              className="font-medium text-emerald-700 hover:underline"
                            >
                              Ver relatório{parada.numero_relatorio ? ` ${parada.numero_relatorio}` : ''}
                            </Link>
                          </>
                        )}
                        {!parada.manutencao_id && parada.cliente_id ? (
                          <span className="text-amber-700"> · Sem relatório</span>
                        ) : null}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-500">
                        {duracaoLabel
                          ? `Parada de ${duracaoLabel} — sem relatório vinculado`
                          : 'Parada sem vínculo (ex.: almoço)'}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {paradasOrdenadas.length === 0 && (
          <p className="text-center text-slate-500 py-8">
            Nenhuma parada registrada nesta rota ainda.
          </p>
        )}
      </div>
    </div>
  );
};

export default RotaDetalhe;
